import React, { useState, useEffect, useMemo, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Hash, Plus, Pencil, Trash2, Lock, Building2, GripVertical } from "lucide-react";
import { colorForShift } from "@/lib/shifts";
import { DEPT_COLORS, deptColorFor } from "@/lib/departments";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  code: "", label: "", start: "08:00", end: "16:00", hours: 8,
  type: "work", color: "regular",
  department: "", department_color: "",
};

export default function ShiftCodesPage({ codes, onChange, facilityId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deptSuggestions, setDeptSuggestions] = useState([]);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [deptOrder, setDeptOrder] = useState([]);      // admin manual order
  const [draggingDept, setDraggingDept] = useState(null);
  const dragOverDeptRef = useRef(null);

  // Load facility's saved department_order so the admin's arrangement sticks
  // across reloads.
  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/facilities/${facilityId}/settings`);
        if (!cancelled) setDeptOrder(Array.isArray(data?.department_order) ? data.department_order : []);
      } catch (e) { console.warn("Failed to load facility settings (department order):", e); }
    })();
    return () => { cancelled = true; };
  }, [facilityId]);

  useEffect(() => {
    if (!facilityId || !open) return;
    (async () => {
      try {
        const { data } = await api.get(`/meta/shift-code-departments?facility_id=${facilityId}`);
        setDeptSuggestions(data || []);
      } catch (e) { console.warn("Failed to load department suggestions:", e); }
    })();
  }, [facilityId, open]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, label: c.label,
      start: c.start || "", end: c.end || "",
      hours: c.hours, type: c.type, color: c.color,
      department: c.department || "",
      department_color: c.department_color || "",
    });
    setOpen(true);
  };

  const computeHours = (s, e) => {
    if (!s || !e) return null;
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60;
    return +(mins / 60).toFixed(2);
  };

  const save = async () => {
    // Require department for work shifts (system codes stay untouched).
    if (form.type === "work" && !form.department.trim()) {
      toast.error("Department is required for work shifts");
      return;
    }
    try {
      const payload = {
        ...form,
        department: form.department.trim() || null,
        department_color: form.department.trim() ? (form.department_color || null) : null,
      };
      if (form.type === "work") {
        const auto = computeHours(form.start, form.end);
        if (auto) payload.hours = auto;
      }
      if (editing) {
        await api.patch(`/meta/shift-codes/${encodeURIComponent(editing.code)}?facility_id=${facilityId}`, payload);
        toast.success("Shift updated");
      } else {
        await api.post(`/meta/shift-codes?facility_id=${facilityId}`, payload);
        toast.success("Shift created");
      }
      setOpen(false);
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete shift ${c.code}?`)) return;
    try {
      await api.delete(`/meta/shift-codes/${encodeURIComponent(c.code)}?facility_id=${facilityId}`);
      toast.success("Shift removed");
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const workCodes = codes.filter(c => c.type === "work");
  const specialCodes = codes.filter(c => c.type !== "work");

  // Group work codes by department using the admin's saved sort order for the
  // department buckets. Any bucket that isn't in `deptOrder` falls to the end
  // in alphabetical order so new departments still show up cleanly.
  const workByDept = useMemo(() => {
    const buckets = new Map();
    workCodes.forEach((c) => {
      const key = (c.department || "").trim() || "__unassigned__";
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(c);
    });
    const entries = Array.from(buckets.entries());
    const orderIdx = new Map(deptOrder.map((n, i) => [n, i]));
    entries.sort(([a], [b]) => {
      if (a === "__unassigned__") return 1;
      if (b === "__unassigned__") return -1;
      const ia = orderIdx.has(a) ? orderIdx.get(a) : Number.POSITIVE_INFINITY;
      const ib = orderIdx.has(b) ? orderIdx.get(b) : Number.POSITIVE_INFINITY;
      if (ia !== ib) return ia - ib;
      return a.localeCompare(b);
    });
    return entries.map(([key, list]) => ({
      key,
      label: key === "__unassigned__" ? "Unassigned" : key,
      color: (list.find((c) => c.department_color)?.department_color) || null,
      codes: list.slice().sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true })),
    }));
  }, [workCodes, deptOrder]);

  const persistDeptOrder = async (nextOrder) => {
    // Save all real department names in the new order (skip the "__unassigned__" bucket).
    const clean = nextOrder.filter((k) => k !== "__unassigned__");
    setDeptOrder(clean);
    try {
      await api.put(`/facilities/${facilityId}/department-order`, { order: clean });
      toast.success("Department order saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save order");
    }
  };

  const onDeptDragStart = (key) => (e) => {
    if (!isAdmin) return;
    setDraggingDept(key);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", key); } catch { /* Safari */ }
  };
  const onDeptDragOver = (key) => (e) => {
    if (!isAdmin || !draggingDept) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    dragOverDeptRef.current = key;
  };
  const onDeptDrop = (key) => (e) => {
    if (!isAdmin || !draggingDept || draggingDept === key) { setDraggingDept(null); return; }
    e.preventDefault();
    const cur = workByDept.map((g) => g.key);
    const from = cur.indexOf(draggingDept);
    const to = cur.indexOf(key);
    if (from === -1 || to === -1) { setDraggingDept(null); return; }
    const next = cur.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDraggingDept(null);
    persistDeptOrder(next);
  };

  const filteredSuggestions = useMemo(() => {
    const q = form.department.trim().toLowerCase();
    if (!q) return deptSuggestions.slice(0, 8);
    return deptSuggestions.filter(d => d.name.toLowerCase().includes(q)).slice(0, 8);
  }, [deptSuggestions, form.department]);

  const pickSuggestion = (s) => {
    setForm(f => ({ ...f, department: s.name, department_color: s.color || f.department_color }));
    setShowDeptDropdown(false);
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="shift-codes-page">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-lg font-display font-semibold">
              <Hash size={18} className="text-blue-600" /> Shift Codes
            </div>
            <div className="text-sm text-slate-500">Create numeric or named shifts, set working hours, and tag the department they cover. Departments drive the grouped 24-hour timeline.</div>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="code-add">
            <Plus size={14} className="mr-1" /> New shift
          </Button>
        </div>

        <div className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
            <span>Work shifts by department</span>
            {isAdmin && (
              <span className="text-[10px] normal-case tracking-normal text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                Drag the handle on the left of any department to reorder
              </span>
            )}
          </div>
          <div className="space-y-3 mb-6">
            {workByDept.map((group) => {
              const dc = group.key !== "__unassigned__" ? deptColorFor(group.label, group.color) : null;
              const isDragging = draggingDept === group.key;
              return (
                <div
                  key={group.key}
                  data-testid={`code-dept-group-${group.key}`}
                  draggable={isAdmin && group.key !== "__unassigned__"}
                  onDragStart={onDeptDragStart(group.key)}
                  onDragOver={onDeptDragOver(group.key)}
                  onDragEnd={() => setDraggingDept(null)}
                  onDrop={onDeptDrop(group.key)}
                  className={`rounded-xl border overflow-hidden transition-all ${
                    isDragging ? "opacity-50 border-blue-400" : "border-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                    {isAdmin && group.key !== "__unassigned__" && (
                      <span
                        className="text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-700"
                        data-testid={`dept-drag-handle-${group.key}`}
                        title="Drag to reorder"
                      >
                        <GripVertical size={14} />
                      </span>
                    )}
                    {dc ? (
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dc.dot}`} />
                    ) : (
                      <Building2 size={12} className="text-amber-600" />
                    )}
                    <span className={`text-[12px] uppercase tracking-widest font-semibold ${dc ? dc.text : "text-slate-700"}`}>
                      {group.label}
                    </span>
                    <span className="text-[10px] text-slate-400 ml-auto tabular-nums">
                      {group.codes.length} shift{group.codes.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-3">
                    {group.codes.map((c) => {
                      const clr = colorForShift(c);
                      return (
                        <div key={c.code} className={`rounded-xl border p-3 ${clr.bg} ${clr.border}`} data-testid={`code-${c.code}`}>
                          <div className="flex items-start justify-between">
                            <div className={`font-display font-bold text-2xl ${clr.text}`}>{c.code}</div>
                            <div className="flex gap-1">
                              <button onClick={() => openEdit(c)} className="text-slate-500 hover:text-blue-600" data-testid={`code-edit-${c.code}`}><Pencil size={14} /></button>
                              <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-600" data-testid={`code-delete-${c.code}`}><Trash2 size={14} /></button>
                            </div>
                          </div>
                          <div className="mt-1 text-sm font-medium text-slate-800">{c.label}</div>
                          <div className="text-xs text-slate-600 tabular-nums mt-0.5">
                            {c.start && c.end ? `${c.start} – ${c.end}` : "—"}{" "}
                            <span className="text-slate-500">· {c.hours}h</span>
                            {c.adjustable_hours && (
                              <span className="ml-1 inline-block text-[9px] uppercase tracking-widest font-bold text-sky-700 bg-sky-100 border border-sky-200 rounded px-1">
                                Adjustable
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {workByDept.length === 0 && <div className="text-sm text-slate-500">No work shifts yet.</div>}
          </div>

          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Time off & special (system)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {specialCodes.map((c) => {
              const clr = colorForShift(c);
              return (
                <Tooltip key={c.code}>
                  <TooltipTrigger asChild>
                    <div className={`rounded-xl border p-3 ${clr.bg} ${clr.border}`} data-testid={`code-${c.code}`}>
                      <div className="flex items-center justify-between">
                        <div className={`font-display font-bold text-lg ${clr.text}`}>{c.code}</div>
                        <Lock size={12} className={clr.text} />
                      </div>
                      <div className={`text-xs ${clr.text}`}>{c.label}</div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">System code · not editable</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit shift" : "New shift"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input placeholder="e.g. 1, 2, 3, A…" value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value })}
                    disabled={!!editing && (editing?.system)}
                    data-testid="code-input" />
                </div>
                <div>
                  <Label>Label</Label>
                  <Input placeholder="e.g. 1st Shift" value={form.label}
                    onChange={e => setForm({ ...form, label: e.target.value })}
                    data-testid="code-label" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start</Label>
                  <Input type="time" value={form.start || ""}
                    onChange={e => setForm({ ...form, start: e.target.value })}
                    data-testid="code-start" />
                </div>
                <div>
                  <Label>End</Label>
                  <Input type="time" value={form.end || ""}
                    onChange={e => setForm({ ...form, end: e.target.value })}
                    data-testid="code-end" />
                </div>
              </div>

              {/* Department + colour */}
              <div className="pt-1">
                <Label>Department</Label>
                <div className="relative">
                  <Input
                    placeholder="e.g. Emergency, PBX, ICU…"
                    value={form.department}
                    onChange={e => { setForm({ ...form, department: e.target.value }); setShowDeptDropdown(true); }}
                    onFocus={() => setShowDeptDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDeptDropdown(false), 150)}
                    data-testid="code-department" />
                  {showDeptDropdown && filteredSuggestions.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-52 overflow-auto" data-testid="dept-suggestions">
                      {filteredSuggestions.map(s => {
                        const dc = deptColorFor(s.name, s.color);
                        return (
                          <button key={s.name} type="button"
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 text-left"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickSuggestion(s)}
                            data-testid={`dept-suggest-${s.name}`}>
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${dc.dot}`} />
                            <span className="font-medium">{s.name}</span>
                            <span className="ml-auto text-[10px] text-slate-400">used {s.count}×</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dept-color-picker">
                  {DEPT_COLORS.map(dc => (
                    <button key={dc.id} type="button"
                      title={dc.label}
                      onClick={() => setForm({ ...form, department_color: dc.id })}
                      className={`w-6 h-6 rounded-full ${dc.dot} border-2 ${form.department_color === dc.id ? "border-slate-900 scale-110" : "border-transparent"} transition-transform`}
                      data-testid={`dept-color-${dc.id}`} />
                  ))}
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">
                  {form.type === "work"
                    ? "Required for work shifts. Type to search past departments in this facility."
                    : "Optional for special codes."}
                </div>
              </div>

              <div className="text-xs text-slate-500">
                Hours are auto-calculated for work shifts (overnight OK).
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="code-save">
                {editing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
